<template>
  <div class="cst-sidebar-wrapper" :class="{ 'is-open': sidebarOpen }">
    <!-- Toggle button (shown when sidebar is collapsed) -->
    <button
      v-if="!sidebarOpen"
      data-qa="sidebar-toggle-button"
      class="sidebar-toggle-btn"
      :aria-label="i18n.t('/hamburgermenu/menu')"
      @click="openSidebar"
    >
      <icon icon="menu" />
    </button>

    <!-- Sidebar panel -->
    <aside ref="sidebar" class="cst-sidebar" :style="{ width: sidebarWidth + 'px' }">
      <!-- Header -->
      <div class="sidebar-header">
        <div class="sidebar-header__top">
          <span class="sidebar-logo">{{ i18n.t('/hamburgermenu/menu') }}</span>
          <button
            data-qa="sidebar-close-button"
            class="sidebar-close-btn"
            :title="i18n.t('/hamburgermenu/close')"
            :aria-label="i18n.t('/hamburgermenu/close')"
            @click="closeSidebar"
          >
            &#x2715;
          </button>
        </div>
      </div>

      <!-- Navigation -->
      <nav class="sidebar-nav">
        <router-link
          data-qa="menu-link-dashboard"
          class="sidebar-item"
          :to="{
            name: 'quote-overview-page',
            params: { cstType: 'b2c', establishmentId: menuEstablishmentId },
          }"
          @click="onNavClick"
        >
          <span class="sidebar-item-icon">&#x1F3E0;</span>
          <span class="sidebar-item-label">{{ i18n.t('/HamburgerMenu/Dashboard') }}</span>
        </router-link>

        <router-link
          data-qa="menu-link-b2b-dashboard"
          class="sidebar-item"
          :to="{
            name: 'quote-overview-page',
            params: { cstType: 'b2b', establishmentId: menuEstablishmentId },
          }"
          @click="onNavClick"
        >
          <span class="sidebar-item-icon">&#x1F3E2;</span>
          <span class="sidebar-item-label">{{ i18n.t('/HamburgerMenu/B2B Dashboard') }}</span>
        </router-link>

        <router-link
          v-show="showPurchaseOrder"
          data-qa="menu-link-purchase-orders"
          class="sidebar-item"
          :to="{ name: 'purchase-orders-page', params: { establishmentId: menuEstablishmentId } }"
          @click="onNavClick"
        >
          <span class="sidebar-item-icon">&#x1F697;</span>
          <span class="sidebar-item-label">{{ i18n.t('/hamburgermenu/purchaseorders') }}</span>
        </router-link>

        <router-link
          v-show="showChangeOrder"
          data-qa="menu-link-change-orders"
          class="sidebar-item"
          :to="{ name: 'change-orders-page', params: { establishmentId: menuEstablishmentId } }"
          @click="onNavClick"
        >
          <span class="sidebar-item-icon fa-solid fa-pencil"></span>
          <span class="sidebar-item-label">{{ i18n.t('/hamburgermenu/changeorders') }}</span>
        </router-link>

        <router-link
          v-show="showBulkFuturePrices"
          data-qa="menu-link-bulk-future-prices"
          class="sidebar-item"
          :to="{ name: 'bulk-future-prices-page' }"
          @click="onNavClick"
        >
          <span class="sidebar-item-icon">&#x1F4C5;</span>
          <span class="sidebar-item-label">{{ i18n.t('/hamburgermenu/bulkfutureprices') }}</span>
        </router-link>

        <router-link
          v-show="canManageDealerSettings"
          data-qa="menu-link-settings"
          class="sidebar-item"
          :to="{ name: 'settings' }"
          @click="onNavClick"
        >
          <span class="sidebar-item-icon">&#x2699;</span>
          <span class="sidebar-item-label">{{ i18n.t('/hamburgermenu/settings') }}</span>
        </router-link>

        <div v-show="showBeheerGroup" class="sidebar-group">
          <button
            data-qa="sidebar-group-beheer-button"
            class="sidebar-item sidebar-group-toggle"
            @click="beheerOpenManual = !beheerOpen"
          >
            <span class="sidebar-item-icon">&#x1F6E0;</span>
            <span class="sidebar-item-label">{{ i18n.t('/hamburgermenu/beheer') }}</span>
            <span class="sidebar-group-chevron" :class="{ 'is-open': beheerOpen }">&#x276F;</span>
          </button>
          <div v-show="beheerOpen" class="sidebar-group-items">
            <router-link
              v-show="canManageAddenda"
              data-qa="menu-link-addenda"
              class="sidebar-item sidebar-sub-item"
              :to="{ name: 'addenda' }"
              @click="onNavClick"
            >
              <span class="sidebar-item-icon">&#x1F4CB;</span>
              <span class="sidebar-item-label">{{ i18n.t('/hamburgermenu/addenda') }}</span>
            </router-link>

            <router-link
              v-show="canInvestigateConfiguration"
              data-qa="menu-link-configuration-investigation"
              class="sidebar-item sidebar-sub-item"
              :to="{ name: 'configuration-investigation' }"
              @click="onNavClick"
            >
              <span class="sidebar-item-icon">&#x1F50E;</span>
              <span class="sidebar-item-label">Configuratie onderzoeken</span>
            </router-link>

            <router-link
              v-show="hasAccessAuthorizationModule"
              data-qa="menu-link-users"
              class="sidebar-item sidebar-sub-item"
              :to="{ name: 'authorization-module' }"
              @click="onNavClick"
            >
              <span class="sidebar-item-icon">&#x1F512;</span>
              <span class="sidebar-item-label">{{ i18n.t('/hamburgermenu/users') }}</span>
            </router-link>

            <router-link
              v-show="canManageLeaseMapping"
              data-qa="menu-link-lease-mapping"
              class="sidebar-item sidebar-sub-item"
              :to="{ name: 'lease-mapping' }"
              @click="onNavClick"
            >
              <span class="sidebar-item-icon">&#x1F517;</span>
              <span class="sidebar-item-label">{{ i18n.t('/hamburgermenu/leasemapping') }}</span>
            </router-link>

            <router-link
              v-show="canManageMedia"
              data-qa="menu-link-media"
              class="sidebar-item sidebar-sub-item"
              :to="{ name: 'media-page', params: { establishmentId: menuEstablishmentId } }"
              @click="onNavClick"
            >
              <span class="sidebar-item-icon">&#128209;</span>
              <span class="sidebar-item-label">{{ i18n.t('/hamburgermenu/media') }}</span>
            </router-link>

            <router-link
              v-show="canManageReports"
              data-qa="menu-link-reports"
              class="sidebar-item sidebar-sub-item"
              :to="{ name: 'reports' }"
              @click="onNavClick"
            >
              <span class="sidebar-item-icon">&#x1F4CA;</span>
              <span class="sidebar-item-label">{{ i18n.t('/hamburgermenu/reports') }}</span>
            </router-link>

            <router-link
              v-show="canManageWarningBanners"
              data-qa="menu-link-warning-banner"
              class="sidebar-item sidebar-sub-item"
              :to="{ name: 'warning-banner' }"
              @click="onNavClick"
            >
              <i class="fas fa-triangle-exclamation sidebar-item-icon" style="color: #f5c518"></i>
              <span class="sidebar-item-label">{{ i18n.t('/hamburgermenu/warningbanner') }}</span>
            </router-link>
          </div>
        </div>

        <div class="sidebar-group">
          <button
            data-qa="sidebar-group-help-button"
            class="sidebar-item sidebar-group-toggle"
            @click="helpOpenManual = !helpOpen"
          >
            <span class="sidebar-item-icon">&#x2753;</span>
            <span class="sidebar-item-label">{{ i18n.t('/HamburgerMenu/Help') }}</span>
            <span class="sidebar-group-chevron" :class="{ 'is-open': helpOpen }">&#x276F;</span>
          </button>
          <div v-show="helpOpen" class="sidebar-group-items">
            <a
              data-qa="menu-link-report-incident"
              class="sidebar-item sidebar-sub-item"
              :href="i18n.t('/HamburgerMenu/Report Incident Url')"
              target="_blank"
            >
              <span class="sidebar-item-icon">&#x26A0;</span>
              <span class="sidebar-item-label">{{ i18n.t('/HamburgerMenu/Report Incident') }}</span>
            </a>
            <router-link
              data-qa="menu-link-faq"
              class="sidebar-item sidebar-sub-item"
              :to="{ name: 'faq' }"
              @click="onNavClick"
            >
              <span class="sidebar-item-icon">&#x2139;</span>
              <span class="sidebar-item-label">{{ i18n.t('/HamburgerMenu/FAQ') }}</span>
            </router-link>
            <router-link
              data-qa="menu-link-instructions"
              class="sidebar-item sidebar-sub-item"
              :to="{ name: 'instructions' }"
              @click="onNavClick"
            >
              <span class="sidebar-item-icon">&#x1F4DA;</span>
              <span class="sidebar-item-label">{{ i18n.t('/HamburgerMenu/Instructions') }}</span>
            </router-link>
          </div>
        </div>
      </nav>

      <!-- Footer / auto-hide + logout -->
      <div class="sidebar-footer">
        <div class="sidebar-autohide" data-qa="sidebar-autohide-checkbox">
          <input
            id="sidebar-autohide-toggle"
            class="sidebar-autohide__input"
            type="checkbox"
            :checked="sidebarAutoHide"
            @change="onAutoHideChange($event.target.checked)"
          />
          <label class="sidebar-autohide__row" for="sidebar-autohide-toggle">
            <span class="sidebar-autohide__text">
              <span class="sidebar-autohide__title">{{ i18n.t('/hamburgermenu/autohide') }}</span>
              <span class="sidebar-autohide__hint">{{
                i18n.t('/hamburgermenu/autohide/hint')
              }}</span>
            </span>
            <span class="sidebar-autohide__switch">
              <span class="sidebar-autohide__knob"></span>
            </span>
          </label>
        </div>

        <a data-qa="menu-logout-button" href="#" class="sidebar-item" @click.prevent="logout">
          <span class="sidebar-item-icon">&#x2192;</span>
          <span class="sidebar-item-label">{{ i18n.t('/HamburgerMenu/Logout') }}</span>
        </a>
      </div>

      <!-- Resize handle -->
      <div class="resize-handle" @mousedown="startResize" />
    </aside>
  </div>
</template>

<script>
import authenticationService from '@/services/authenticationService'
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import { mapGetters, mapState, mapActions } from 'vuex'
import { ref } from 'vue'

const MIN_WIDTH = 385
const MAX_WIDTH = 480

export default {
  components: { Icon },
  setup() {
    return { sidebar: ref(null) }
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      helpOpenManual: false,
      beheerOpenManual: false,
    }
  },
  computed: {
    ...mapGetters({
      hasAccessPurchaseOrders: 'user/hasAccessPurchaseOrders',
      hasAccessBulkFuturePrices: 'user/hasAccessBulkFuturePrices',
      dealerSettings: 'user/dealerSettings',
      hasAccessChangeOrders: 'user/hasAccessChangeOrders',
      establishments: 'establishments/availableEstablishments',
      hasAccessAuthorizationModule: 'user/hasAccessAuthorizationModule',
      canManageWarningBanners: 'user/canManageWarningBanners',
      canManageMedia: 'user/canManageMedia',
      canManageDealerSettings: 'user/canManageDealerSettings',
      canManageAddenda: 'user/canManageAddenda',
      canManageReports: 'user/canManageReports',
      canManageLeaseMapping: 'user/canManageLeaseMapping',
      canInvestigateConfiguration: 'user/canInvestigateConfiguration',
      sidebarOpen: 'ui/sidebarOpen',
      sidebarWidth: 'ui/sidebarWidth',
      sidebarAutoHide: 'ui/sidebarAutoHide',
    }),
    ...mapState('user', ['dealerId']),
    currentEstablishmentId() {
      return this.$route.params.establishmentId
    },
    defaultEstablishmentId() {
      return this.establishments?.length > 0 ? this.establishments[0].EstablishmentNumber : null
    },
    menuEstablishmentId() {
      return this.currentEstablishmentId || this.defaultEstablishmentId || this.dealerId
    },
    showPurchaseOrder() {
      return this.hasAccessPurchaseOrders
    },
    showChangeOrder() {
      return this.dealerSettings.changeOrderEnabled && this.hasAccessChangeOrders
    },
    showBulkFuturePrices() {
      return this.hasAccessBulkFuturePrices
    },
    showBeheerGroup() {
      return (
        this.canManageMedia ||
        this.hasAccessAuthorizationModule ||
        this.canManageWarningBanners ||
        this.canManageAddenda ||
        this.canManageReports ||
        this.canManageLeaseMapping ||
        this.canInvestigateConfiguration
      )
    },
    isBeheerRoute() {
      return [
        'media-page',
        'authorization-module',
        'warning-banner',
        'addenda',
        'reports',
        'lease-mapping',
        'configuration-investigation',
      ].includes(this.$route.name)
    },
    isHelpRoute() {
      return ['faq', 'instructions'].includes(this.$route.name)
    },
    beheerOpen() {
      return this.isBeheerRoute || this.beheerOpenManual
    },
    helpOpen() {
      return this.isHelpRoute || this.helpOpenManual
    },
  },
  watch: {
    $route() {
      if (!this.isBeheerRoute) this.beheerOpenManual = false
      if (!this.isHelpRoute) this.helpOpenManual = false
      this.$nextTick(() => this.scrollActiveItemIntoView())
    },
  },
  mounted() {
    this.$nextTick(() => this.scrollActiveItemIntoView())
  },
  methods: {
    ...mapActions('ui', ['openSidebar', 'closeSidebar', 'resizeSidebar', 'setSidebarAutoHide']),
    onNavClick() {
      if (this.sidebarAutoHide) this.closeSidebar()
    },
    onAutoHideChange(value) {
      this.setSidebarAutoHide(value)
    },
    scrollActiveItemIntoView() {
      const nav = this.$el.querySelector('.sidebar-nav')
      if (!nav) return
      // Find the active router-link — it may be a sub-item inside a v-show group
      const active = nav.querySelector('.sidebar-item.router-link-active')
      if (!active) return
      // Walk up the DOM to compute offset relative to nav, since
      // getBoundingClientRect gives 0 for elements inside hidden v-show containers
      let offsetTop = 0
      let el = active
      while (el && el !== nav) {
        offsetTop += el.offsetTop
        el = el.offsetParent
      }
      const itemHeight = active.offsetHeight
      const navHeight = nav.clientHeight
      const currentScroll = nav.scrollTop
      // Only scroll if the item is outside the visible area
      if (offsetTop < currentScroll) {
        nav.scrollTo({ top: offsetTop - 8, behavior: 'smooth' })
      } else if (offsetTop + itemHeight > currentScroll + navHeight) {
        nav.scrollTo({ top: offsetTop + itemHeight - navHeight + 8, behavior: 'smooth' })
      }
    },
    async logout() {
      await authenticationService.logout()
    },
    startResize(event) {
      const startX = event.clientX
      const startWidth = this.sidebarWidth
      const onMouseMove = (e) => {
        const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + (e.clientX - startX)))
        this.resizeSidebar(newWidth)
      }
      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
        document.body.style.userSelect = ''
        document.body.style.cursor = ''
      }
      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'col-resize'
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    },
  },
}
</script>

<style lang="scss" scoped>
$transition-speed: 0.25s;

.cst-sidebar-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: z-index(menu);
  pointer-events: none;
}

.sidebar-toggle-btn {
  position: fixed;
  top: 16px;
  left: 0.75rem;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: z-index(menu);
  pointer-events: auto;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  color: $gray-darker;
  outline: none;

  &:hover {
    opacity: 0.7;
  }
}

.cst-sidebar {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  background: $white;
  border-right: 1px solid $gray-base;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.08);
  transform: translateX(-100%);
  transition: transform $transition-speed cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 385px;
  max-width: 480px;
  pointer-events: auto;
  overflow: hidden;

  .is-open & {
    transform: translateX(0);
  }
}

.sidebar-header {
  display: flex;
  flex-direction: column;
  padding: 0 1rem;
  background: $gray-lightest;
  border-bottom: 1px solid $gray-base;
  flex-shrink: 0;

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 72px;
  }
}

.sidebar-logo {
  font-size: 0.7rem;
  font-weight: $font-weight-bold;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: $gray-dark;
}

.sidebar-close-btn {
  background: none;
  border: none;
  color: $gray-dark;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.25rem 0.4rem;
  border-radius: 3px;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: $gray-lighter;
    color: $gray-darker;
  }
}

.sidebar-nav {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: $gray-light transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: $gray-light;
    border-radius: 2px;
  }
}

.sidebar-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.1rem 1.25rem;
  color: $gray-darker;
  text-decoration: none;
  font-size: 1rem;
  border-left: 3px solid transparent;
  transition: background-color 0.35s cubic-bezier(0.23, 1, 0.32, 1);
  white-space: nowrap;
  overflow: hidden;
  cursor: pointer;

  &:hover {
    background-color: #dae2e5;
    text-decoration: none;
    color: $gray-darker;
  }

  &.router-link-active {
    background-color: $gray-lightest;
    border-left-color: $color-primary;
    font-weight: $font-weight-bold;

    .sidebar-item-icon {
      opacity: 1;
    }
  }
}

.sidebar-item-icon {
  font-size: 1rem;
  opacity: 0.45;
  flex-shrink: 0;
  width: 1.25rem;
  text-align: center;
  transition: opacity 0.15s;
}

.sidebar-item-label {
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-footer {
  flex-shrink: 0;
  border-top: 1px solid $gray-base;

  .sidebar-item {
    color: $gray-dark;

    &:hover {
      color: $gray-darker;
      background-color: $gray-lighter;
    }
  }
}

// Auto-hide preference: label + hint on the left, switch on the right.
// A real checkbox drives it (kept off-screen) so the row stays keyboard
// operable and reports its state to assistive tech; the pill and knob are
// only its visual skin.
$switch-width: 40px;
$switch-height: 22px;
$switch-knob: 16px;
$switch-inset: 3px;

.sidebar-autohide {
  background: $gray-lightest;
  border-bottom: 1px solid $gray-base;

  &__input {
    @include sr-only-styles;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 0.875rem;
    margin: 0;
    padding: 0.75rem 1.25rem 0.8rem;
    cursor: pointer;
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__title {
    font-size: 0.85rem;
    color: $gray-darker;
  }

  &__hint {
    font-size: 0.72rem;
    color: $gray-dark;
  }

  &__switch {
    position: relative;
    flex: 0 0 auto;
    margin-left: auto;
    width: $switch-width;
    height: $switch-height;
    border-radius: $switch-height * 0.5;
    background: $gray-light;
    transition: background 0.18s ease;
  }

  &__knob {
    position: absolute;
    top: $switch-inset;
    left: $switch-inset;
    width: $switch-knob;
    height: $switch-knob;
    border-radius: 50%;
    background: $white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    transition: transform 0.18s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &__input:checked + &__row &__switch {
    background: $color-primary;
  }

  &__input:checked + &__row &__knob {
    transform: translateX($switch-width - $switch-knob - $switch-inset * 2);
  }

  &__input:focus-visible + &__row &__switch {
    outline: 2px solid $color-primary;
    outline-offset: 2px;
  }
}

.resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  width: 4px;
  height: 100%;
  cursor: col-resize;
  background: transparent;
  transition: background 0.2s;

  &:hover,
  &:active {
    background: $color-primary;
    opacity: 0.4;
  }
}

.sidebar-group-toggle {
  width: 100%;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font-size: 1rem;
}

.sidebar-group-chevron {
  margin-left: auto;
  display: inline-block;
  transition: transform 0.2s ease;
  font-size: 0.7rem;
  opacity: 0.5;

  &.is-open {
    transform: rotate(90deg);
  }
}

.sidebar-sub-item {
  padding-left: 2.5rem;
}
</style>
